/**
 * @module app.cm
 */

import { autoinject, observable, BindingEngine, Disposable, ICollectionObserverSplice } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { ITasksConfig, IQueuesConfig, IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { allActionsSucceeded, apiCall } from 'app/shared/lib/essette-api';
import { TaskService } from 'app/core/services/task-service';
import { CaseService } from 'app/cm/services/case-service';
import { AssessmentService } from 'app/core/services/assessment-service';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { I18N } from 'aurelia-i18n';
import { TaskWithLocalState } from 'app/core/models/local-state-extensions';
import { getReference } from 'app/shared/models/essette-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { localSort } from 'app/shared/lib/sorting';
import { deepClone } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { guid } from 'app/shared/lib/guid';

interface TaskWithLocalStateAndCase extends TaskWithLocalState {
    case?: CM.Case;
}

interface CorrespondenceLetterWithCase extends Core.CorrespondenceLetter {
    case?: CM.Case;
}

interface TaskWithMember extends Core.Task {
    member?: CD.Member;
}

interface DuplicateTask {
    memberDescription?: string;
    taskDescription?: string;
}

@autoinject()
export class AddTasks extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    @bindable.objectNull
    protected tasksConfig: ITasksConfig | null = null;

    @bindable.objectNull
    protected queuesConfig: IQueuesConfig | null = null;

    @bindable.objectNull
    protected assessmentsConfig: IAssessmentsConfig | null = null;

    /** the value of the autocomplete textbox */
    @observable
    public taskSearchQuery: string | null = null;

    public currentTasks: TaskWithMember[] = [];

    /** Holds cases associated with a household case */
    protected cases: CM.Case[] = [];
    protected selectedCases: CM.Case[] = [];
    protected localSorting = new Sorting();

    private selectedTasksSubscription: Disposable | null = null;

    /** Holds the value of the selected radio input. Switches between how user finds a task */
    protected taskOption: string = 'selectTasks';

    /** task definition lookup (select and autocomplete results) */
    protected taskDefinitions: Core.TaskDefinition[] = [];

    /** array of search results from API for autocomplete (not currently used) */
    protected taskSearchResults: Core.TaskDefinition[] = [];

    /** array of tasks user has selected from either the multi-select or autocomplete */
    protected selectedTasks: Core.TaskDefinition[] = [];

    /** Array of tasks the users has selected (standard and custom) to be saved */
    protected insertTasks: TaskWithLocalState[] = [];

    /** the value of the custom task input */
    protected customTaskText: string | null = null;
    protected customTaskTextMax: number = 200;

    /** Permission that controls whether a user can enter a custom task */
    protected hasAddCustomTasks: boolean = false;
    /** Permission that controls whether a custom task can be added to the library (N/A to interventions) */
    protected hasAddCustomTaskToLibrary: boolean = false;

    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly taskService: TaskService,
        private readonly caseService: CaseService,
        private readonly assessmentService: AssessmentService,
        private readonly mcgService: McgInformedService,
        private readonly bindingEngine: BindingEngine,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        protected readonly transitional: TransitionalManager,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();
    }

    protected bind(): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        if(!this.tasksConfig) { throw new Error('Bindable property [tasks-config] was not set'); }
        if(!this.queuesConfig) { throw new Error('Bindable property [queues-config] was not set'); }
        if(!this.assessmentsConfig) { throw new Error('Bindable property [assessments-config] was not set'); }

        const targetType = this.tasksConfig.targetType;
        if(!targetType) { throw new Error('[tasks-config] did not define a targetType'); }

        this.taskService.configure(this.tasksConfig);

        this.reload();
    }

    protected attached(): void {
        this.observeSelectedTasks();
    }

    protected detached(): void {
        if(this.selectedTasksSubscription) {
            this.selectedTasksSubscription.dispose();
        }
    }


    ////****************** Events ***************************************************////
    /** When selectedTasks array changes (select tasks or search tasks), the detected changes are added/removed from insertTasks (the tasks to be saved) */
    protected selectedTasksChanged(splices: ICollectionObserverSplice<Core.TaskDefinition>[]): void {
        if(this.insertTasks.length === 0 && this.selectedTasks.length === 0) return;
        this.updateTasksAdded(splices, this.selectedTasks);
    }


    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadLookups();
        await this.loadTasks();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAddCustomTasks = this.accountContext.hasPermission('CM.CaseTask.Insert.AddCustom');
        this.hasAddCustomTaskToLibrary = this.accountContext.hasPermission('CM.CaseTask.Insert.AddToLibrary');
    }

    private observeSelectedTasks() {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedTasksSubscription) {
            this.selectedTasksSubscription.dispose();
        }
        this.selectedTasksSubscription = this.bindingEngine
            .collectionObserver(this.selectedTasks) // observe the selectedTasks array, which changes in the multi-select Task Definition Select element
            .subscribe(this.selectedTasksChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedTasksChanged method

        // subscription disposed of in detached()
    }

    private updateTasksAdded(splices: ICollectionObserverSplice<Core.TaskDefinition>[], selectedArray: Core.TaskDefinition[]): void {
        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var tasksAdded = selectedArray.slice(splice.index, splice.index + splice.addedCount);
            if(tasksAdded.length > 0) {
                tasksAdded.forEach(
                    task => {
                        const t: TaskWithLocalState = {
                            isCustom: false,
                            description: task.description,
                            definition: {
                                id: task.id,
                                type: task.type,
                                disallowActivation: task.disallowActivation
                            },
                            delegated: {
                                queue: getReference(task.defaultQueue)
                            },
                            _state: { isSaved: false, addToLibrary: false }
                        };
                        //TODO don't just swallow this error do something about it
                        if(this.taskService.config.addTargetProperty) { this.taskService.config.addTargetProperty(t); }
                        this.insertTasks.push(t);
                    });
            }

            if(splice.removed.length) {
                (splice.removed as Core.TaskDefinition[]).forEach(
                    task => {
                        const i = this.insertTasks.find(t => (!!t.definition && t.definition.id) === task.id);
                        if(i) {
                            this.insertTasks.splice(this.insertTasks.indexOf(i), 1);
                        }
                    });
            }
        }
    }

    protected getSelectText(): string {
        if(this.i18n) {
            const { length } = this.selectedTasks;
            if(length > 1) {
                // INCOMPLETE: this doesn't seem to use the i18n pluralization functions properly
                let itemString = this.i18n.tr(`${this.tasksConfig!.labelKey}_plural`);
                return this.i18n.tr('ui:labels.selected', { what: itemString, number: length, count: length });
            }
        }
        return '';
    }

    /** Called when a user clicks on an auto-complete search result */
    protected addAutoCompleteTask(task: Core.TaskDefinition): void {
        this.selectedTasks.push(task); // will trigger selectedTasksChanged(), and add the task to selectedTasks
        this.taskSearchQuery = '';

        this.taskSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected isCustomTaskTextValid(text: string): boolean {
        return !!(text && /[^\s]/.test(text));
    }

    /** Called when a user types in a custom task description and clicks the button */
    protected addCustomTask(): void {
        const task: TaskWithLocalState = {
            isCustom: true,
            description: this.customTaskText,
            _state: { isSaved: false, addToLibrary: false }
        };
        if(this.taskService.config.addTargetProperty) { this.taskService.config.addTargetProperty(task) };

        if(!this.isCustomTaskTextValid(this.customTaskText || '')) {
            return;
        }
        else {
            this.insertTasks.push(task);
        }

        this.customTaskText = null;
    }

    protected filterTasks = (task: Core.Task): boolean => {
        if(this.selectedTasks.some(t => t.id === task.id)) {
            return false;
        }
        if(this.tasksConfig && this.tasksConfig.allowDuplicates) { return true; }
        if(this.currentTasks.length === 0 && !task) { return false; }
        return !this.currentTasks.some(t => task.id === (t.definition && t.definition.id));
    }

    /** Takes a task from the insertTasks grid and constructs a new copy to be sent to the API */
    protected async buildTaskWithCaseData(memberCase: CM.Case, task: TaskWithLocalState): Promise<TaskWithLocalStateAndCase> {
        if(!task._state) { task._state = {}; }

        const saveTask: TaskWithLocalStateAndCase = {
            isCustom: task.isCustom,
            due: !!task.due && !!task.due.date ? { date: task.due.date } : undefined,
            definition: deepClone(task.definition!),
            case: getReference(memberCase)!
        };

        // only add description to request if task is custom
        if(task.isCustom) saveTask.description = task.description;

        if(task.definition?.type?.code === 'Core.Assessment' && task.definition?.type?.argument?.id) {
            const externalSystemId = await this.loadAssessmentExternalSystemId(task.definition.type.argument.id);
            if(externalSystemId) saveTask.externalSystem = { id: externalSystemId };
        }

        if(this.hasAddCustomTaskToLibrary && !!task._state.addToLibrary) {
            // The API will add a task to the library if the task doesn't have a definition and "isCustom = false"
            saveTask.isCustom = false;
            delete saveTask.definition;
        }

        if(task.delegated && task.delegated.queue) {
            saveTask.delegated = {
                queue: getReference(task.delegated.queue),
                to: task.delegated.to && task.delegated.queue && task.delegated.queue.delegated ? {
                    id: task.delegated.to.id,
                    type: task.delegated.queue.delegated.type
                } : undefined
            }
        }

        return saveTask;
    }

    private buildDuplicateTasksMessage(duplicateTasks: DuplicateTask[]): string {
        let message = '';
        duplicateTasks.forEach(dt => {
            message += `${dt.memberDescription}: ${dt.taskDescription}\n`;
        });
        return message;
    }


    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.saveTasks();
    }

    public reset(): void {
        this.selectedTasks.splice(0);
        this.selectedCases.splice(0);
        this.insertTasks.splice(0);
        if(this.householdCaseId) {
            this.reload();
        }
        this.taskSearchQuery = '';
        this.taskSearchResults.splice(0);
        this.taskOption = 'selectTasks';
    }


    ////****************** Service Calls ********************************************////
    /** Saves each task in a batch and makes additional API calls as needed. */
    @apiCall
    private async saveTasks(): Promise<boolean> {
        const duplicateTasks: DuplicateTask[] = [];

        if(this.currentTasks.length > 0) {
            this.selectedCases.forEach(c => {
                this.insertTasks.forEach(t => {
                    if(this.tasksConfig && this.tasksConfig.allowDuplicates && this.currentTasks.some(ct => (ct.definition && ct.definition.id) === (t.definition && t.definition.id) && (ct.member && ct.member.id) === (c.member && c.member.id))) {
                        duplicateTasks.push({ memberDescription: c.member?.name?.full || '', taskDescription: t.description || '' });
                    }
                })
            });

            /** Show confirmation to add duplicate tasks */
            if(duplicateTasks.length > 0) {
                const message: string = this.i18n.tr('ui:messages.duplicate.confirm', { count: duplicateTasks.length, what: this.i18n.tr(this.tasksConfig!.labelKey, { count: duplicateTasks.length }) });
                const result = await confirmation.showCustom(message, this.buildDuplicateTasksMessage(duplicateTasks), confirmation.options.StandardConfirm);
                if(result.dialogResponse === confirmation.options.CANCEL) {
                    //canceling means we just stop
                    return Promise.resolve(false);
                }
            }
        }

        // store one version of the array for the API !inserts and one for the followup letter/assessment !inserts
        const tasksWithCaseData: TaskWithLocalStateAndCase[] = [];
        const tasksForApi: TaskWithLocalStateAndCase[] = [];
        for(const c of this.selectedCases) {
            for(const t of this.insertTasks) {
                const taskWithCaseData = await this.buildTaskWithCaseData(c, t);
                tasksWithCaseData.push(taskWithCaseData);
                tasksForApi.push({ ...taskWithCaseData, definition: getReference(taskWithCaseData.definition) });
            }
        }

        const progressToast = this.toaster.standard.batch('core:task.$', tasksForApi.length);
        progressToast.show();

        let success = false;
        let externalContentSuccess = true;
        try {
            this.state.beginSave();
            const responses = await this.caseService.insertTasksToCasesBatch(tasksForApi, progressToast);
            success = allActionsSucceeded(responses);
            if(success) {
                const newLetters: CorrespondenceLetterWithCase[] = [];
                const newExternalContents: string[] = [];

                // for each successful API response, check if there's any additional corresponding calls needing to be made
                tasksWithCaseData.forEach((t, index) => {
                    if(t.definition?.type?.code === "Core.CorrespondenceLetter" && t.due === undefined && !t.definition?.disallowActivation) {
                        const newLetter: CorrespondenceLetterWithCase = {
                            template: { id: t.definition.type.argument?.id },
                            case: { id: t.case?.id },
                            task: { id: responses[index].id }
                        };
                        newLetters.push(newLetter);
                    } else if(t.definition?.type?.code === 'Core.Assessment' && t.externalSystem?.id) {
                        newExternalContents.push(responses[index].id!);
                    }
                });

                if(newLetters.length > 0) {
                    await this.saveSmartTaskLetters(newLetters);
                }

                if(newExternalContents.length > 0) {
                    externalContentSuccess = await this.saveAssessmentExternalContents(newExternalContents);
                }
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }

        return success && externalContentSuccess;
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const taskDefinitionIncludes: string[] = [
            'defaultQueue',
            'type'
        ]

        const caseIncludes: string[] = [
            'member'
        ]

        try {
            this.state.beginLoad();

            const [taskDefinitions, cases] = await Promise.all(
                [
                    this.taskService.loadTaskDefinitions(options, taskDefinitionIncludes),
                    this.caseService.loadCasesByHouseholdCase(this.householdCaseId, options, caseIncludes)
                ]);

            if(!taskDefinitions.isSuccessful || !cases.isSuccessful) return;

            this.taskDefinitions = taskDefinitions.results;
            this.cases = cases.results;
            // use local sorting to sort cases by member description
            this.localSorting.entries = [
                {
                    reference: 'member.description',
                    direction: SortDirection.Ascending
                }
            ];
            localSort(this.cases, this.localSorting);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadTasks(): Promise<void> {
        const includes: string[] = ['definition', 'member'];

        try {
            this.state.beginLoad();
            const response = await this.taskService.loadTasksByTarget(this.householdCaseId!, { all: true }, includes);
            this.currentTasks = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    /** Called as user types into the auto-complete task search box */
    @apiCall
    protected async searchTasks(newValue: string): Promise<void> {
        if(!newValue) {
            if(this.taskSearchResults) this.taskSearchResults.splice(0);
            return;
        }

        const includes: string[] = ['metadata.created', 'metadata.delegated', 'metadata.completed.byUser', 'definition'];
        try {
            this.state.beginLoad();
            const response = await this.taskService.searchDefinitions(newValue, undefined, includes);
            this.taskSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAssessmentExternalSystemId(assessmentId: string): Promise<string | null> {
        try {
            this.state.beginLoad();

            if(!this.assessmentsConfig) return null;
            this.assessmentService.configure(this.assessmentsConfig);

            const response = await this.assessmentService.loadAssessment(assessmentId);
            if(!response.isSuccessful || response.results.length === 0) return null;

            const assessment: Core.Assessment = response.results[0];
            const externalSystemId = assessment.externalSystem?.id;
            if(externalSystemId) return externalSystemId;

            return null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveSmartTaskLetters(letters: CorrespondenceLetterWithCase[]): Promise<boolean> {
        if(letters.length === 0) return false;

        const progressToast = this.toaster.standard.batch('core:correspondenceLetter.$', letters.length);
        progressToast.show();

        let success = false;
        try {
            this.state.beginSave();

            const responses = await this.taskService.insertSmartTaskLettersBatch(letters, progressToast);
            success = allActionsSucceeded(responses);

            if(success) {
                for(const response of responses) {
                    try {
                        const snippetResponse = await this.transitional.post(`?action=generateLetter&LetterID=${response.id}&ModuleCode=${this.tasksConfig?.moduleCode}`);
                        this.toaster.standard.saved('core:correspondenceSnippet.$', snippetResponse.isSuccessful)
                    } catch(error) {
                        this.toaster.error().message(this.i18n.tr('core:correspondenceLetter.notifications.failedSnippets')).show();
                    }
                }
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }

        return success;
    }

    @apiCall
    private async saveAssessmentExternalContents(taskIds: string[]): Promise<boolean> {
        if(taskIds.length === 0) return false;

        let success = false;
        try {
            this.state.beginSave();

            const externalContents: Core.AssessmentAttachmentExternalContent[] = [];

            for(const taskId of taskIds) {
                const attachmentId = await this.loadAssessmentAttachment(taskId);
                if(!attachmentId) continue;
                const content = await this.loadMcgAssessmentContent(attachmentId);
                if(!content) continue;

                const externalContent: Core.AssessmentAttachmentExternalContent = {
                    assessmentAttachment: { id: attachmentId },
                    externalContentType: { code: 'AssessmentJson' },
                    content: JSON.stringify(content),
                    mediaType: { code: 'data.json' }
                }

                externalContents.push(externalContent);
            }

            const responses = await this.assessmentService.insertAttachmentExternalContentBatch(externalContents);
            success = allActionsSucceeded(responses);

            return success;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAssessmentAttachment(taskId: string): Promise<string | null> {
        try {
            this.state.beginLoad();

            const response = await this.assessmentService.loadAttachmentByTask(taskId, undefined, undefined);
            if(!response.isSuccessful || response.results.length === 0) return null;

            const attachment: Core.AssessmentAttachment = response.results[0];
            if(attachment.id) return attachment.id;

            return null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMcgAssessmentContent(attachmentId: string): Promise<string | null> {
        try {
            this.state.beginLoad();

            const response = await this.mcgService.getAssessmentContent(attachmentId);
            if(!response.isSuccessful) return null;

            return response.result;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
